import { useState, useEffect, useCallback } from "react";
import apiClient, { BASE_URL } from "../../services/apiClient";

export const useAdminData = () => {
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [userDetails, setUserDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all admin data
  const fetchAdminData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const usersResponse = await apiClient.get("/Users");
      setUsers(usersResponse.data || []);

      const userDetailsResponse = await apiClient.get("/users/details");
      setUserDetails(userDetailsResponse.data || []);

      const groupsResponse = await apiClient.get("/Groups");
      setGroups(groupsResponse.data || []);
    } catch (err) {
      setError(err.message || "Failed to fetch admin data");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial data load
  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  // Listen for refresh events (triggered after delete operations)
  useEffect(() => {
    const handleRefresh = (event) => {
      const { deletedItemId, itemType } = event.detail || {};

      if (itemType === "group") {
        // Remove the deleted group from local state immediately
        setGroups((prev) => prev.filter((group) => group.id !== deletedItemId));
      } else if (itemType === "user") {
        // Remove the deleted user from local state immediately
        setUsers((prev) => prev.filter((user) => user.id !== deletedItemId));
      }

      // Optionally refetch all data to ensure consistency
      // fetchAdminData();
    };

    window.addEventListener("refreshAdminData", handleRefresh);
    return () => {
      window.removeEventListener("refreshAdminData", handleRefresh);
    };
  }, []);

  // Update item locally (for save operations)
  const updateItemLocally = useCallback(
    (
      entityHandler,
      formDataOrServerItem,
      itemId,
      isCreateOperation = false
    ) => {
      if (isCreateOperation) {
        // For create operations, formDataOrServerItem is the server response
        const serverItem = formDataOrServerItem;

        if (entityHandler.entityType === "group") {
          setGroups((prev) =>
            prev.map((group) => (group.id === itemId ? serverItem : group))
          );
          return serverItem;
        } else if (entityHandler.entityType === "user") {
          setUsers((prev) =>
            prev.map((user) => (user.id === itemId ? serverItem : user))
          );
          return serverItem;
        }
      } else {
        // For update operations, formDataOrServerItem is form data
        const formData = formDataOrServerItem;
        const updatedItem = entityHandler.createUpdatedItem(formData, itemId);

        if (entityHandler.entityType === "group") {
          setGroups((prev) =>
            prev.map((group) =>
              group.id === itemId ? { ...group, ...updatedItem } : group
            )
          );
          return { ...groups.find((g) => g.id === itemId), ...updatedItem };
        } else if (entityHandler.entityType === "user") {
          setUsers((prev) =>
            prev.map((user) =>
              user.id === itemId ? { ...user, ...updatedItem } : user
            )
          );
          return { ...users.find((u) => u.id === itemId), ...updatedItem };
        }
      }

      return null;
    },
    [groups, users]
  );

  // Add new item locally (for create operations)
  const addItemLocally = useCallback((itemType, newItem) => {
    if (itemType === "group") {
      setGroups((prev) => [newItem, ...prev]); // Add to top of list
    } else if (itemType === "user") {
      setUsers((prev) => [newItem, ...prev]); // Add to top of list
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    groups,
    users,
    userDetails,
    loading,
    error,
    clearError,
    updateItemLocally,
    addItemLocally,
    refreshData: fetchAdminData,
    setGroups,
    setUsers,
  };
};
